/* eslint-disable no-underscore-dangle, react/no-danger, import/no-extraneous-dependencies */
import { ApolloProvider } from "@apollo/client";
import { GTMProvider } from "@elgorditosalsero/react-gtm-hook";
import type { ISnippetsParams } from "@elgorditosalsero/react-gtm-hook/dist/models/GoogleTagManager";
import { ThemeProvider } from "@emotion/react";
import FundamenteiAppHead from "components/app/FundamenteiAppHead";
import theme from "components/css/theme";
import AmplitudeTrackPageViews from "components/metrics/AmplitudeTrackPageViews";
import isBrowser from "lib/utils/isBrowser";
import { memoize } from "lodash/fp";
import type { AppContext } from "next/app";
import App from "next/app";
import localFont from "next/font/local";
import { Router } from "next/router";
import { parseCookies } from "nookies";
import { RecoilRoot } from "recoil";
import type { IAppInitialProps } from "types/AppInitialProps";
import { AuthProvider } from "../components/auth/Auth";
import UnsupportedBrowser from "../components/errors/UnsupportedBrowser";
import { getInitialSearchStateFromQuery } from "../components/screener/searchState";
import { SearchStateProvider } from "../lib/algolia/SearchStateContext";
import { AmplitudeProvider } from "../lib/amplitude/AmplitudeProvider";
import withServerResponse from "../lib/http/withServerResponse";
import isBrowserSupported from "../lib/isBrowserSupported";
import withApollo from "../lib/withApollo";
import "../styles/globals.css";

const aeonik = localFont({
  variable: "--font-aeonik",
  src: [
    { path: "../public/fonts/aeonik/Aeonik-Thin.woff2", weight: "100", style: "normal" },
    { path: "../public/fonts/aeonik/Aeonik-ThinItalic.woff2", weight: "100", style: "italic" },

    { path: "../public/fonts/aeonik/Aeonik-Light.woff2", weight: "200", style: "normal" },
    { path: "../public/fonts/aeonik/Aeonik-LightItalic.woff2", weight: "200", style: "italic" },

    { path: "../public/fonts/aeonik/Aeonik-Regular.woff2", weight: "300", style: "normal" },
    { path: "../public/fonts/aeonik/Aeonik-RegularItalic.woff2", weight: "300", style: "italic" },

    { path: "../public/fonts/aeonik/Aeonik-Regular.woff2", weight: "400", style: "normal" },
    { path: "../public/fonts/aeonik/Aeonik-RegularItalic.woff2", weight: "400", style: "italic" },

    { path: "../public/fonts/aeonik/Aeonik-Medium.woff2", weight: "500", style: "normal" },
    { path: "../public/fonts/aeonik/Aeonik-MediumItalic.woff2", weight: "500", style: "italic" },

    { path: "../public/fonts/aeonik/Aeonik-Bold.woff2", weight: "800", style: "normal" },
    { path: "../public/fonts/aeonik/Aeonik-BoldItalic.woff2", weight: "800", style: "italic" },

    // { path: "../public/fonts/aeonik/Aeonik-Black.woff2", weight: "900", style: "normal" },
    // { path: "../public/fonts/aeonik/Aeonik-BlackItalic.woff2", weight: "900", style: "italic" },
  ],
});

// https://github.com/facebookexperimental/Recoil/issues/733#issuecomment-925072943
if (process.env.NODE_ENV === "development") {
  const mutedConsole = memoize((console) => ({
    ...console,
    error: (...args: any[]) => {
      if (Array.isArray(args) && args[0] instanceof Error && args[0].message.includes("Duplicate atom key")) {
        return;
      }
      if (Array.isArray(args) && args[0] && typeof args[0] === "string" && args[0].includes("Duplicate atom key")) {
        return;
      }
      console.error(...args);
    },
  }));

  global.console = mutedConsole(global.console);
}

// NOTE: amplitude-js doesn't have SSR support. GH issue here: https://github.com/amplitude/Amplitude-JavaScript/issues/110
const amplitude = isBrowser() ? require("amplitude-js") : null;

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    fbq?: (...args: any) => void;
    gtag?: (...args: any) => void;
  }
}

class FundamenteiApp extends App<IAppInitialProps> {
  public componentDidMount() {
    Router.events.on("routeChangeComplete", () => {
      if (process.env.FUNDAMENTEI_ENV !== "production") {
        return;
      }

      // Facebook Pixel
      if (window.fbq && typeof window.fbq === "function") {
        window.fbq("track", "PageView");
      }

      // Google Analytics
      if (window.gtag && typeof window.gtag === "function") {
        window.gtag("config", "G-23P2YLWQWN", {
          page_location: window.location.href,
          page_path: window.location.pathname,
          page_title: window.document.title,
          page_hash: window.location.hash,
          page_referrer: window.document.referrer,
        });
      }
    });
  }

  public static async getInitialProps({ Component, ctx }: AppContext) {
    let pageProps = {};

    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx);
    }

    const userAgent = ctx.req ? ctx.req.headers["user-agent"] : navigator.userAgent;

    return {
      pageProps,
      cookies: parseCookies(ctx),
      userAgent,
      query: ctx.query,
      isSSRReadyToRender: true,
    };
  }

  public render() {
    const { Component, userAgent, pageProps, apollo, cookies, query } = this.props;

    // Checks if the User is using a dead browser
    if (!isBrowserSupported(userAgent)) {
      return (
        <>
          <FundamenteiAppHead />
          <UnsupportedBrowser />
        </>
      );
    }

    return (
      <RecoilRoot>
        <AmplitudeProvider amplitudeInstance={getAmplitudeInstance()} apiKey={process.env.AMPLITUDE_API_KEY as string}>
          <>
            {process.env.NODE_ENV === "production" && <AmplitudeTrackPageViews />}

            <ApolloProvider client={apollo}>
              <AuthProvider cookies={cookies}>
                <>
                  <FundamenteiAppHead />

                  <style jsx={true} global={true}>
                    {`
                      html {
                        font-family: ${aeonik.style.fontFamily};
                      }
                    `}
                  </style>

                  <SearchStateProvider initialSearchState={getInitialSearchStateFromQuery(query)}>
                    <GTMProvider state={getGTMState()}>
                      <ThemeProvider theme={theme}>
                        <Component {...pageProps} />
                      </ThemeProvider>
                    </GTMProvider>
                  </SearchStateProvider>
                </>
              </AuthProvider>
            </ApolloProvider>
          </>
        </AmplitudeProvider>
      </RecoilRoot>
    );
  }
}

function getGTMState() {
  let gtmState: ISnippetsParams = {
    // fundamentei.com Web container
    id: "GTM-TKT26QB",
  };

  const isDevelopment = process.env.NODE_ENV === "development";
  if (isDevelopment) {
    gtmState = {
      ...gtmState,
      environment: {
        gtm_auth: "siko-tvnEiRaAkAsYBhkJg",
        gtm_preview: "env-8",
        // gtm_cookies_win: "x",
      },
    };
  }

  return gtmState;
}

function getAmplitudeInstance() {
  if (!amplitude) {
    return null;
  }

  if (amplitude.default && typeof amplitude.default.getInstance === "function") {
    return amplitude.default.getInstance();
  }

  if (typeof amplitude.getInstance === "function") {
    return amplitude.getInstance();
  }

  return null;
}

export default withServerResponse(withApollo(FundamenteiApp) as any);
